

import { Box, Button, MenuItem, Paper, Popover, TextField, Typography } from '@mui/material';
import { BoxFC, BoxFR } from 'components/BoxCustom';
import DataGridCellExpand from 'components/DataGridCellExpand/DataGridCellExpand';
import React, { useCallback, useState, useContext, useEffect, useMemo } from 'react';
import { addSumRow, getSumColorClassName, sxSumColorClass } from 'utils/highlightSumColor';
import { financeialReportColumns } from './columns';
import { GlobalStateContext } from 'contexts/GlobalStateContext';
import DateTimeSelect from 'components/DateTimeSelect';
import SearchButton from 'components/buttons/SearchButton';
import ClearButton from 'components/buttons/ClearButton';
import { initFilterData, clearFilterData } from './initData';
import { jobOrderApiNm, jobOrderColNm } from 'constants/jobOrderConstant';
import ComboBox from 'components/ComboBox';
import DailyJobDialogV3 from 'pages/Menu1/DailyJob/DailyJobDialogV3';
import { blue, green, grey } from '@mui/material/colors';
import { PrintRounded } from 'resource/muiImport';
import UpdateDateTimeDialog from 'pages/Menu1/DailyJob/UpdateDateTimeDialog';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { printFinanceReport } from './printFinanceReport';
import TagBox from 'components/TagBox';
import { originBottomCenter, originTopCenter } from 'constants/popoverConstant';
import ReceiptPaper from 'components/ReceiptPaper';

let selectedId = null;
let colNm = "";
let oldSelectedData = null;
let selectedJobOrdIds = [];
let lastFilter = {};
let selectedRow = {};

const FinancialReport = () => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogUpdateDateTimeOpen, setDialogUpdateDateTimeOpen] = useState(false)
  const [filterData, setFilterData] = useState({ ...initFilterData })
  const [anchorEl, setAnchorEl] = useState(null);

  const popOverOpen = Boolean(anchorEl);

  const modDataTable = useMemo(() => {
    return addSumRow({
      dataTable,
      sumColNms: ["IncE1", "IncE2", "IncE3", "IncE4", "IncE1018", "IncOthAmnt", "IncTotal", "ItmSumCostAmnt", "PayAmnt",
        "ItmE87", "ItmE88", "ItmE1032", "ItmE1000", "ItmE1004", "ItmE1011", "ItmE1001", "ItmE1020", "ItmE1017", "ItmE1012", "ItmOthAmnt",
        "ScE1"],
      sumColTotal: "Tel"
    })
  }, [dataTable])
  const getData = useCallback((filter) => {
    const postData = {
      ...filter,
      JobTagIds: filter.JobTags.map(jt => jt.id),
    }
    ax.post("/branch/getFinancialReport", postData).then(value => {
      if (value.data) {
        lastFilter = filter
        const reuslts = []
        for (const data of value.data) {
          const incE1 = data.Incs.find(inc => inc.ExpId === 1)
          const incE2 = data.Incs.find(inc => inc.ExpId === 2)
          const incE3 = data.Incs.find(inc => inc.ExpId === 3)
          const incE4 = data.Incs.find(inc => inc.ExpId === 4)
          const incE5 = data.Incs.find(inc => inc.ExpId === 5)
          const incE1018 = data.Incs.find(inc => inc.ExpId === 1018)
          const incEOth = data.Incs.filter(inc => ![1, 2, 3, 4, 5, 1018].includes(inc.ExpId))
          data.IncE1 = incE1 ? incE1.Total : 0
          data.IncE2 = incE2 ? incE2.Total : 0
          data.IncE3 = +(incE3?.Total || 0) + +(incE5?.Total || 0)
          data.IncE4 = incE4 ? incE4.Total : 0
          data.IncE1018 = incE1018 ? incE1018.Total : 0
          data.IncOthAmnt = incEOth.reduce((sum, inc) => sum + inc.Total, 0)
          data.IncOthAmntNm = incEOth.map(inc => inc.ExpNm).join(", ")

          const itmE87 = data.Itms.find(itm => itm.ExpId === 87 )
          const itmE88 = data.Itms.find(itm => itm.ExpId === 88 )
          const itmE1032 = data.Itms.find(itm => itm.ExpId === 1032 )
          const itmE1000 = data.Itms.find(itm => itm.ExpId === 1000 && itm.IsCost === 1)
          const itmE1004 = data.Itms.find(itm => itm.ExpId === 1004 && itm.IsCost === 1)
          const itmE1011 = data.Itms.find(itm => itm.ExpId === 1011 && itm.IsCost === 1)
          const itmE1001 = data.Itms.find(itm => itm.ExpId === 1001 && itm.IsCost === 1)
          const itmE1020 = data.Itms.find(itm => itm.ExpId === 1020 && itm.IsCost === 1)
          const itmE1017 = data.Itms.find(itm => itm.ExpId === 1017 && itm.IsCost === 1)
          const itmE1012 = data.Itms.find(itm => itm.ExpId === 1012 && itm.IsCost === 1)
          const itmEOth = data.Itms.filter(itm => ![87, 88, 1032, 1000, 1004, 1011, 1001, 1020, 1017, 1012].includes(itm.ExpId) && itm.IsCost === 1)
          data.ItmE87 = itmE87 ? itmE87.Amnt : 0
          data.ItmE88 = itmE88 ? itmE88.Amnt : 0
          data.ItmE1032 = itmE1032 ? itmE1032.Amnt : 0
          data.ItmE1000 = itmE1000 ? itmE1000.Amnt : 0
          data.ItmE1004 = itmE1004 ? itmE1004.Amnt : 0
          data.ItmE1011 = itmE1011 ? itmE1011.Amnt : 0
          data.ItmE1001 = itmE1001 ? itmE1001.Amnt : 0
          data.ItmE1020 = itmE1020 ? itmE1020.Amnt : 0
          data.ItmE1017 = itmE1017 ? itmE1017.Amnt : 0
          data.ItmE1012 = itmE1012 ? itmE1012.Amnt : 0
          data.ItmOthAmnt = itmEOth.reduce((sum, itm) => sum + itm.Amnt, 0)
          data.ItmOthAmntNm = itmEOth.map(itm => itm.ExpNm).join(", ")

          const scE1 = data.Scs.find(sc => sc.ExpId === 1)
          data.ScE1 = scE1 ? scE1.Total : 0
          data.id = data.JobOrdId
          reuslts.push(data)
        }
        setDataTable(reuslts)
      }
    })
  }, [ax])

  const textFieldProps = useCallback((name) => ({
    size: "small",
    sx: { width: 150 },
    label: jobOrderColNm[name],
    value: filterData[name] || "",
    onChange: (e) => setFilterData(o => ({ ...o, [name]: e.target.value || null })),
  }), [filterData])

  const handleCellClick = useCallback((params, e) => {
    if (params.field === 'ShowDetail') {
      selectedId = params.id
      setDialogOpen(true)
    } else if (params.field === 'FinDte') {
      colNm = 'FinDte'
      oldSelectedData = params.value
      selectedJobOrdIds = selectionModel.includes(params.id) ? selectionModel : [params.id]
      setDialogUpdateDateTimeOpen(true)
    } else if (params.field === "RcptNm") {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      selectedRow = params.row
    }
  }, [selectionModel])

  const handleRowDoubleClick = useCallback((params) => {
    selectedId = params.id
    setDialogOpen(true)
  }, [])

  const handlePrintFinanceReportBuy = useCallback(() => {
    printFinanceReport({
      msData,
      dataTable: dataTable.filter(data => selectionModel.includes(data.JobOrdId)),
      mode: 'BUY'
    })
  }, [msData, dataTable, selectionModel])

  const handlePrintFinanceReportSell = useCallback(() => {
    printFinanceReport({
      msData,
      dataTable: dataTable.filter(data => selectionModel.includes(data.JobOrdId)),
      mode: 'SELL'
    })
  }, [msData, dataTable, selectionModel])

  const updateColumn = useCallback(async (colName, data, jobOrdIds, withFamily) => {
    const jsondata = {
      JobOrdIds: jobOrdIds,
      ColValues: data,
      WithFamily: withFamily,
      LogRmk: `แก้ไขข้อมูล ${jobOrderColNm[colName] || colName}`
    }
    await ax.post(jobOrderApiNm.updateJobOrderColumn, jsondata)
    getData(lastFilter);

  }, [ax, getData])

  const updateDateTime = useCallback((colName, data, jobOrdIds) => {
    const withFamily = colName === "FinDte" ? 1 : null
    if (colName === "TakeDteSt") {
      data.IsRdyTake = data[colName] ? 1 : 0
    }
    updateColumn(colName, data, jobOrdIds, withFamily)
    setDialogUpdateDateTimeOpen(false);
  }, [updateColumn])

  const handlePopOverClose = useCallback(() => {
    setAnchorEl(null);
  }, [])
  useEffect(() => {
    getData({ ...initFilterData })
  }, [getData])

  return (
    <BoxFC height='100%'>
      <BoxFR flexWrap="wrap" justifyContent="flex-end">
        <Button variant='contained' onClick={handlePrintFinanceReportBuy}>
          <PrintRounded sx={{ mr: 1 }} />รายงานซื้อ
        </Button>
        <Button variant='contained' onClick={handlePrintFinanceReportSell}>
          <PrintRounded sx={{ mr: 1 }} />รายงานขาย
        </Button>
        <Box flex={1} />
        <TextField {...textFieldProps("PVNo")} label="PV No." />
        <TextField {...textFieldProps("RVNo")} label="RV No." />
        <TextField {...textFieldProps("RcptNo")} label="Receipt No." />
        <TextField {...textFieldProps("InvNo")} label="Invoice No." />
        <TextField {...textFieldProps("JobNo")} />
        <TextField {...textFieldProps("Bkg")} />
        <TextField {...textFieldProps("ContNo")} />
        <TextField {...textFieldProps("JobOrdId")} />
        <ComboBox sx={{ width: 150 }} options={msData.subContractCombo} label="รถร่วม"
          selectedId={filterData.ScId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, ScId: id }))}
        />
        <ComboBox sx={{ width: 150 }} options={msData.cusCombo} label={jobOrderColNm.CusSNm}
          selectedId={filterData.CusId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, CusId: id }))}
        />
        <ComboBox sx={{ width: 150 }} options={msData.jobCombo} label={jobOrderColNm.JobSNm}
          selectedId={filterData.JobId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, JobId: id }))}
        />
        <DateTimeSelect sx={{ width: 180 }} label={jobOrderColNm.AptTm}
          // initRadioValue="MONTH"
          start={filterData.AptTmSt} end={filterData.AptTmEn}
          setSelectDate={(st, en) => { setFilterData(o => ({ ...o, AptTmSt: st, AptTmEn: en })) }}
          getData={() => { getData(filterData) }}
        />
        <DateTimeSelect sx={{ width: 180 }} label="วันที่ Invoice"
          initRadioValue="MONTH"
          start={filterData.InvDteSt} end={filterData.InvDteEn}
          setSelectDate={(st, en) => { setFilterData(o => ({ ...o, InvDteSt: st, InvDteEn: en })) }}
          getData={() => { getData(filterData) }}
        />
        <DateTimeSelect sx={{ width: 180 }} label="วันที่ เอกสารรับแจ้งหนี้"
          start={filterData.XpssDocDteSt} end={filterData.XpssDocDteEn}
          setSelectDate={(st, en) => { setFilterData(o => ({ ...o, XpssDocDteSt: st, XpssDocDteEn: en })) }}
          getData={() => { getData(filterData) }}
        />
        <TextField {...textFieldProps("PaySts")} label="สถานะใบงาน" select sx={{ width: 180 }}>
          <MenuItem value={null}>ไม่ระบุ</MenuItem>
          <MenuItem value="NOTFIN">ยังไม่ลงจบ</MenuItem>
          <MenuItem value="FIN">ลงจบแล้ว ยังไม่แจ้งหนี้</MenuItem>
          <MenuItem value="INV">แจ้งหนี้แล้ว ยังไม่รับชำระ</MenuItem>
          <MenuItem value="PAID">รับชำระแล้ว</MenuItem>
        </TextField>
        <TagBox label={jobOrderColNm.JobTags} tblAbbr="JB" sx={{ width: 200 }}
          value={filterData.JobTags} setValue={(ids) => setFilterData(o => ({ ...o, JobTags: ids }))} />
        <SearchButton onClick={(() => getData(filterData))} />
        <ClearButton onClick={(() => setFilterData({ ...clearFilterData }))} />
      </BoxFR>
      <Box height='100%' sx={{
        ...sxSumColorClass,
        '& .MuiDataGrid-row.Mui-selected .itm': { backgroundColor: blue[100] },
        '& .MuiDataGrid-row:hover .itm': { backgroundColor: blue[100] },
        '& .itm': { backgroundColor: blue[50] },
        '& .MuiDataGrid-row.Mui-selected .inc': { backgroundColor: green[100] },
        '& .MuiDataGrid-row:hover .inc': { backgroundColor: green[100] },
        '& .inc': { backgroundColor: green[50] }
      }}>
        <DataGridCellExpand
          disableSelectionOnClick={true}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          hideFooter
          rows={modDataTable}
          columns={financeialReportColumns}
          onRowDoubleClick={handleRowDoubleClick}
          onCellClick={handleCellClick}
          getRowClassName={getSumColorClassName}
          experimentalFeatures={{ rowPinning: true }}
          pinnedRows={{ bottom: [modDataTable.find(row => row.id === 0)] }}
          initialState={{
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'ShowDetail', 'RcptNm'],
            },
          }}
        />
      </Box>

      <Popover
        open={popOverOpen}
        anchorEl={anchorEl}
        onClose={handlePopOverClose}
        anchorOrigin={originBottomCenter}
        transformOrigin={originTopCenter}>
        <BoxFC sx={{ gap: 1, p: 1, bgcolor: grey[1000] }}>
          <ReceiptPaper rcptData={selectedRow} sx={{ width: 500 }} />
          <Paper sx={{ p: 2, width: 500 }}>
            <Typography variant="body2"><strong>รายละเอียด: </strong></Typography>
            {selectedRow.Dscp}
          </Paper>
        </BoxFC>
      </Popover>
      <UpdateDateTimeDialog
        dialogOpen={dialogUpdateDateTimeOpen}
        setDialogOpen={setDialogUpdateDateTimeOpen}
        colName={colNm}
        oldData={oldSelectedData}
        jobOrdIds={selectedJobOrdIds}
        updateColumn={updateDateTime} />
      <DailyJobDialogV3
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        selectedId={selectedId} />
    </BoxFC>
  );
}

export default FinancialReport